import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  ShoppingBag, Package, Truck, CheckCircle, Clock, Seal, CookingPot,
  MapPin, Eye, ArrowCounterClockwise, Copy, ArrowSquareOut, FilePdf, Download, Star
} from '@phosphor-icons/react';
import { useCart } from '../../context/CartContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-800', icon: Clock },
  confirmed: { label: 'Confirmée', color: 'bg-sky-100 text-sky-800', icon: Seal },
  preparing: { label: 'En préparation', color: 'bg-blue-100 text-blue-800', icon: CookingPot },
  shipping: { label: 'En livraison', color: 'bg-purple-100 text-purple-800', icon: Truck },
  delivered: { label: 'Livrée', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  cancelled: { label: 'Annulée', color: 'bg-red-100 text-red-800', icon: Package },
};

const statusFlow = ['pending', 'confirmed', 'preparing', 'shipping', 'delivered'];

const AccountOrders = ({ storeSlug: propStoreSlug }) => {
  const { storeSlug: paramStoreSlug } = useParams();
  const storeSlug = propStoreSlug || paramStoreSlug || 'mrg';
  const { addItem, setIsOpen } = useCart();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [store, setStore] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/s/${storeSlug}`).then(r => setStore(r.data)).catch(() => {});
  }, [storeSlug]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/account/orders`, { withCredentials: true });
        setOrders(response.data);
      } catch { }
      finally { setLoading(false); }
    };
    fetchOrders();
  }, []);

  const colors = { primary: store?.identity?.primary_color || '#c2185b' };

  const handleReorder = (order) => {
    if (!order.items?.length) return;
    order.items.forEach(item => {
      addItem({
        id: item.product_id || item.id || `reorder-${Math.random()}`,
        name: item.name,
        price: item.price || item.total / (item.quantity || 1),
        image: item.image,
        quantity: item.quantity || 1,
        slug: item.slug
      });
    });
    setIsOpen(true);
    toast.success(`${order.items.length} article(s) ajouté(s) au panier`);
  };

  const copyTracking = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code de suivi copié !');
  };

  return (
    <div className="space-y-5" data-testid="account-orders">
      <div>
        <h2 className="text-xl font-bold">Mes commandes</h2>
        <p className="text-slate-400 text-[13px] mt-0.5">Suivez et recommandez vos commandes</p>
      </div>

      {loading ? (
        <div className="py-12 text-center">
          <div className="animate-spin rounded-full h-7 w-7 border-b-2 mx-auto" style={{ borderColor: colors.primary }} />
        </div>
      ) : orders.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-12 text-center">
            <ShoppingBag size={48} className="mx-auto text-slate-200 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Aucune commande</h3>
            <p className="text-slate-400 text-[13px]">Vous n'avez pas encore passé de commande</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => {
            const status = statusConfig[order.status] || statusConfig.pending;
            const StatusIcon = status.icon;

            return (
              <Card key={order.id} className="border-0 shadow-sm" data-testid={`order-${order.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center shrink-0">
                        <StatusIcon size={20} weight="fill" style={{ color: colors.primary }} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="font-semibold text-[14px]">{order.order_number}</p>
                          <Badge className={`${status.color} text-[10px]`}>{status.label}</Badge>
                        </div>
                        <p className="text-[12px] text-slate-400">
                          {new Date(order.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })} · {order.items?.length} article(s)
                        </p>
                        {order.tracking_code && (
                          <p className="text-[11px] text-slate-400 mt-0.5 font-mono">
                            Suivi : {order.tracking_code}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="font-bold text-[16px]">{order.total?.toFixed(2)} EUR</span>
                      <Button variant="outline" size="sm" className="text-[11px] h-8" onClick={() => setSelectedOrder(order)} data-testid={`view-order-${order.id}`}>
                        <Eye size={14} className="mr-1" /> Détails
                      </Button>
                      <Button size="sm" className="text-[11px] h-8 text-white" style={{ backgroundColor: colors.primary }}
                        onClick={() => handleReorder(order)} data-testid={`reorder-${order.id}`}>
                        <ArrowCounterClockwise size={14} className="mr-1" /> Recommander
                      </Button>
                    </div>
                  </div>

                  {/* Item thumbnails */}
                  <div className="mt-3 pt-3 border-t border-slate-100 flex gap-2 overflow-x-auto">
                    {order.items?.slice(0, 5).map((item, idx) => (
                      <div key={idx} className="w-12 h-12 bg-slate-50 rounded-lg overflow-hidden shrink-0">
                        {item.image ? (
                          <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package size={16} className="text-slate-300" />
                          </div>
                        )}
                      </div>
                    ))}
                    {order.items?.length > 5 && (
                      <div className="w-12 h-12 bg-slate-50 rounded-lg flex items-center justify-center shrink-0">
                        <span className="text-[11px] font-medium text-slate-400">+{order.items.length - 5}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Order Detail Modal with Tracking Timeline */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Commande {selectedOrder?.order_number}</DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-5">
              <div className="flex items-center gap-3 flex-wrap">
                <Badge className={statusConfig[selectedOrder.status]?.color || 'bg-slate-100'} data-testid="order-detail-status">
                  {statusConfig[selectedOrder.status]?.label || selectedOrder.status}
                </Badge>
                <span className="text-[12px] text-slate-400">
                  {new Date(selectedOrder.created_at).toLocaleDateString('fr-FR')}
                </span>
              </div>

              {/* Tracking code + link */}
              {selectedOrder.tracking_code && (
                <div className="bg-slate-50 rounded-xl p-4" data-testid="order-tracking-info">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[11px] text-slate-400 uppercase font-bold tracking-wider">Code de suivi</p>
                      <p className="font-mono text-lg font-bold mt-0.5" style={{ color: colors.primary }}>{selectedOrder.tracking_code}</p>
                    </div>
                    <div className="flex gap-1.5">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => copyTracking(selectedOrder.tracking_code)} data-testid="copy-tracking-btn">
                        <Copy size={16} />
                      </Button>
                      <Link to={`/suivi/${selectedOrder.tracking_code}`} target="_blank">
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="open-tracking-page-btn">
                          <ArrowSquareOut size={16} />
                        </Button>
                      </Link>
                    </div>
                  </div>

                  {/* Mini status progression */}
                  <div className="flex items-center gap-1 mt-4">
                    {statusFlow.map((st, i) => {
                      const isFilled = statusFlow.indexOf(selectedOrder.status) >= i;
                      return (
                        <React.Fragment key={st}>
                          <div
                            className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${i === statusFlow.indexOf(selectedOrder.status) ? 'ring-2 ring-offset-1' : ''}`}
                            style={{
                              backgroundColor: isFilled ? colors.primary : '#e2e8f0',
                              color: isFilled ? '#fff' : '#94a3b8',
                              '--tw-ring-color': colors.primary,
                            }}
                            title={statusConfig[st]?.label}
                          >
                            {React.createElement(statusConfig[st]?.icon || Package, { size: 11, weight: 'fill' })}
                          </div>
                          {i < statusFlow.length - 1 && (
                            <div className="flex-1 h-0.5 rounded-full" style={{ backgroundColor: isFilled ? colors.primary : '#e2e8f0' }} />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                  <div className="flex justify-between mt-1 px-0.5">
                    {statusFlow.map(st => (
                      <span key={st} className="text-[9px] text-slate-400">{statusConfig[st]?.label?.split(' ').pop()}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* History timeline */}
              {selectedOrder.history?.length > 0 && (
                <div data-testid="order-history-timeline">
                  <p className="text-[12px] font-bold text-slate-600 mb-2">Historique</p>
                  <div className="relative ml-2 space-y-0">
                    <div className="absolute left-1.5 top-1 bottom-1 w-0.5 bg-slate-200" />
                    {[...selectedOrder.history].reverse().map((entry, i) => (
                      <div key={i} className="relative pl-7 pb-3 last:pb-0">
                        <div
                          className="absolute left-0 top-1 w-3.5 h-3.5 rounded-full border-2 border-white"
                          style={{ backgroundColor: i === 0 ? colors.primary : '#cbd5e1' }}
                        />
                        <div className="flex items-center gap-2">
                          <span className="text-[12px] font-medium">{statusConfig[entry.status]?.label || entry.status}</span>
                          <span className="text-[10px] text-slate-400">
                            {new Date(entry.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        {entry.notes && <p className="text-[11px] text-slate-400">{entry.notes}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Items */}
              <div>
                <h4 className="text-[13px] font-semibold mb-2">Articles</h4>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg">
                      <div className="w-12 h-12 bg-white rounded-lg overflow-hidden shrink-0">
                        {item.image ? <img src={item.image} alt="" className="w-full h-full object-cover" /> :
                          <div className="w-full h-full flex items-center justify-center"><Package size={18} className="text-slate-300" /></div>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-medium truncate">{item.name}</p>
                        <p className="text-[11px] text-slate-400">Qté: {item.quantity}</p>
                      </div>
                      <p className="text-[13px] font-semibold">{(item.total || item.price * item.quantity)?.toFixed(2)} EUR</p>
                    </div>
                  ))}
                </div>
              </div>

              {selectedOrder.shipping_address && (
                <div>
                  <h4 className="text-[13px] font-semibold mb-2 flex items-center gap-1.5">
                    <MapPin size={14} style={{ color: colors.primary }} /> Livraison
                  </h4>
                  <div className="text-[12px] text-slate-500 bg-slate-50 p-3 rounded-lg">
                    <p className="font-medium text-slate-700">{selectedOrder.shipping_address?.first_name} {selectedOrder.shipping_address?.last_name}</p>
                    <p>{selectedOrder.shipping_address?.address}</p>
                    <p>{selectedOrder.shipping_address?.postal_code} {selectedOrder.shipping_address?.city}</p>
                  </div>
                </div>
              )}

              <div className="border-t pt-3 space-y-1.5">
                {selectedOrder.subtotal > 0 && (
                  <div className="flex justify-between text-[12px] text-slate-500">
                    <span>Sous-total</span><span>{selectedOrder.subtotal?.toFixed(2)} EUR</span>
                  </div>
                )}
                <div className="flex justify-between text-[12px] text-slate-500">
                  <span>Livraison</span><span>{selectedOrder.shipping_cost > 0 ? `${selectedOrder.shipping_cost?.toFixed(2)} EUR` : 'Offert'}</span>
                </div>
                <div className="flex justify-between font-bold text-[15px] pt-1.5 border-t">
                  <span>Total</span><span style={{ color: colors.primary }}>{selectedOrder.total?.toFixed(2)} EUR</span>
                </div>
              </div>

              <Button className="w-full text-white" style={{ backgroundColor: colors.primary }}
                onClick={() => { handleReorder(selectedOrder); setSelectedOrder(null); }}>
                <ArrowCounterClockwise size={16} className="mr-2" /> Recommander cette commande
              </Button>

              {/* Invoice download */}
              <Button
                variant="outline"
                className="w-full justify-center gap-2"
                data-testid="customer-download-invoice"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = `${API_URL}/api/s/${storeSlug}/orders/${selectedOrder.order_number}/invoice`;
                  link.download = `facture_${selectedOrder.order_number}.pdf`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                <FilePdf size={16} /> Telecharger la facture
              </Button>

              {/* Google review CTA for delivered orders */}
              {selectedOrder.status === 'delivered' && store?.settings?.google_review_url && (
                <a href={store.settings.google_review_url} target="_blank" rel="noopener noreferrer" data-testid="order-google-review">
                  <Button variant="outline" className="w-full border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 hover:text-amber-900 gap-2">
                    <Star size={16} weight="fill" className="text-amber-500" /> Laisser un avis Google
                  </Button>
                </a>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountOrders;
